import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { exercisesApi, type Exercise, type ExerciseSource, type ExerciseType } from '@/api/exercises'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { EXERCISE_TYPE_GROUPS } from './catalog'

/** Порядок типов берём из каталога конструктора: два списка разъехались бы */
const TYPES: ExerciseType[] = Object.values(EXERCISE_TYPE_GROUPS).flat()

type Props = {
  open: boolean
  /**
   * Тема, в которую ставим задание. Не задана — диалог ничего не сохраняет сам
   * и только отдаёт выбранное задание в `onPick`.
   */
  themeId?: number
  themeTitle?: string
  /** Заголовок диалога: у выдачи задания на уроке он свой */
  title?: string
  /** Подсказка над списком: у выдачи вне программы предупреждение другое */
  hint?: string
  /**
   * Задания, уже стоящие в теме. Повторная ссылка на то же задание даёт две
   * одинаковые строки в теме и 422 с сервера — такие строки показываем
   * помеченными и не даём кликнуть.
   */
  attachedIds?: number[]
  /**
   * Только готовые задания. Черновик в тему поставить можно (его ещё допишут),
   * а выдать ученику — нет: сервер отвечает 422, и строка должна быть погашена
   * до нажатия.
   */
  requireReady?: boolean
  onClose: () => void
  /**
   * Передумал: тот же шаг «добавить блок», но другой тип. Вход в диалоги один,
   * и из пикера должен быть выход обратно к теории и словарю.
   */
  onSwitchType?: (next: 'theory' | 'vocabulary') => void
  /** Задание встало в тему — родителю нужно перечитать структуру */
  onAttached?: () => void
  /**
   * Режим выбора: что делать с заданием, решает вызывающий (например, выдаёт
   * его на урок). Диалог ждёт промис и сам показывает ошибку, оставаясь
   * открытым, — иначе учитель теряет из виду список банка.
   */
  onPick?: (exercise: Exercise) => void | Promise<void>
}

/**
 * Выбор задания из банка: в тему программы или под действие вызывающего.
 *
 * Задание живёт в банке учителя, а тема на него ссылается: создавать задание
 * «внутри темы» нельзя — иначе одно и то же упражнение размножается копиями по
 * темам, и правка доезжает только до одной из них. Поэтому и кнопки «создать»
 * здесь нет: новое задание заводится только на странице банка заданий.
 *
 * Второй режим — `onPick`: тот же банк, но без записи в тему. Через него урок
 * выдаёт задание, не стоящее ни в одной теме, — копировать ради этого весь
 * список с фильтрами и пагинацией незачем.
 */
export function ExerciseBankPickerDialog({
  open,
  themeId,
  themeTitle,
  title,
  hint,
  attachedIds = [],
  requireReady = false,
  onClose,
  onSwitchType,
  onAttached,
  onPick,
}: Props) {
  const { t } = useTranslation()

  const [items, setItems] = useState<Exercise[]>([])
  const [total, setTotal] = useState(0)
  const [type, setType] = useState<ExerciseType | ''>('')
  const [source, setSource] = useState<ExerciseSource | ''>('')
  // Поле и запрос разведены: запрос уходит по дебаунсу, иначе каждая буква —
  // отдельный поход в банк, а список моргает ответами на предыдущие буквы
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(20)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Не флаг, а id строки в полёте: пока запрос идёт, список погашен целиком, а
  // спиннер стоит именно на нажатой строке
  const [submittingId, setSubmittingId] = useState<number | null>(null)
  // Двойник в ссылке: обработчик читает состояние из замыкания своего рендера,
  // и двойной клик по строке успевал уйти на сервер дважды — тема получала два
  // одинаковых блока, а урок две выдачи
  const submittingRef = useRef(false)
  // Отказ выдачи приходит, когда список уже пролистан к нажатой строке, а
  // сообщение стоит над списком: без прокрутки учитель его не видел и
  // принимал отказ за молчание (выдача ученику без кабинета)
  const errorRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [error])

  // Номер запроса: фильтры и поиск переключают быстро, и без него в списке
  // оказывался тот ответ, что пришёл последним, а не тот, что соответствует
  // фильтру
  const requestRef = useRef(0)

  const attached = useMemo(() => new Set(attachedIds), [attachedIds])

  const load = useCallback(async () => {
    const request = ++requestRef.current
    setLoading(true)
    setError(null)
    try {
      // page у API 1-based, у MUI-пагинации 0-based — сдвиг делает вызывающий
      const result = await exercisesApi.listBank({ type, source, q: search, page: page + 1, perPage })

      if (request !== requestRef.current) return

      setItems(result.items)
      setTotal(result.total)
    } catch (err) {
      if (request === requestRef.current) setError(getErrorMessage(err) || t('common.error'))
    } finally {
      if (request === requestRef.current) setLoading(false)
    }
  }, [type, source, search, page, perPage, t])

  useEffect(() => {
    if (!open) return

    void load()
  }, [open, load])

  // Новый запрос — всегда с первой страницы: на третьей странице результат
  // другого поиска чаще всего пуст, и учителю кажется, что банк пустой
  useEffect(() => {
    // Страницу сбрасываем только при реальной смене запроса: иначе правка
    // пробела в поле (и первый рендер диалога) выкидывали бы учителя со второй
    // страницы на первую
    if (searchInput === search) return

    const timer = setTimeout(() => {
      setSearch(searchInput)
      setPage(0)
    }, 400)

    return () => clearTimeout(timer)
  }, [searchInput, search])

  // Страница за пределами выдачи: задание с неё поставили в тему и оно ушло из
  // списка, сузился фильтр или вырос размер страницы. MUI на таком page рисует
  // пустой диапазон, а учитель видит пустой банк — возвращаемся на последнюю
  // существующую страницу сами.
  useEffect(() => {
    if (!open || loading || page === 0 || page * perPage < total) return

    setPage(total === 0 ? 0 : Math.ceil(total / perPage) - 1)
  }, [loading, open, page, perPage, total])

  // Фильтры прошлого открытия к другой теме не относятся: пикер открывают из
  // строки конкретной темы и ждут полный банк, а не вчерашний поиск
  useEffect(() => {
    if (open) return

    setType('')
    setSource('')
    setSearchInput('')
    setSearch('')
    setPage(0)
    setError(null)
  }, [open])

  const submit = async (exercise: Exercise) => {
    if (submittingRef.current) return

    submittingRef.current = true
    setSubmittingId(exercise.id)
    setError(null)
    try {
      if (onPick) {
        await onPick(exercise)
      } else if (themeId != null) {
        await exercisesApi.attachToTheme(themeId, exercise.id)
        onAttached?.()
      }
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      submittingRef.current = false
      setSubmittingId(null)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        {title ?? t('exercises.picker.title', 'Задание в тему')}
        {themeTitle && (
          <Typography variant="body2" color="text.secondary">
            {themeTitle}
          </Typography>
        )}
      </DialogTitle>

      {/* Тип блока — тот же шаг, что и в диалоге теории: выбрав «Задание»,
        учитель должен иметь право передумать, не закрывая диалог.
        pb обязателен: без него граница DialogContent dividers ложится вплотную
        на селект и читается как его подчёркивание */}
      {onSwitchType && (
        <Box sx={{ px: 3, pt: 1, pb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('programs.fields.elementType', 'Тип блока')}</InputLabel>
            <Select
              label={t('programs.fields.elementType', 'Тип блока')}
              value="exercise"
              onChange={(event) => {
                const next = event.target.value as 'theory' | 'vocabulary' | 'exercise'

                if (next !== 'exercise') onSwitchType(next)
              }}
            >
              <MenuItem value="theory">{t('programs.elementTypes.theory', 'Теория')}</MenuItem>
              <MenuItem value="vocabulary">{t('programs.elementTypes.vocabulary', 'Словарь темы')}</MenuItem>
              <MenuItem value="exercise">{t('programs.elementTypes.exercise', 'Задание')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          {hint ??
            t(
              'exercises.picker.hint',
              'Задание живёт в банке, тема на него ссылается: правка задания отразится во всех темах и домашках, где оно стоит.',
            )}
        </Alert>

        {error && (
          <Alert ref={errorRef} severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          {/* Поиск первым: в банке сотни заданий, и нужное ищут по названию, а
            фильтры лишь сужают выдачу — спрятанный за ними поиск учитель
            просто не находил */}
          <TextField
            size="small"
            label={t('common.search', 'Поиск')}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            sx={{ flex: 1 }}
          />

          <TextField
            select
            size="small"
            label={t('exercises.picker.type', 'Тип задания')}
            value={type}
            onChange={(event) => {
              setType(event.target.value as ExerciseType | '')
              setPage(0)
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('exercises.picker.anyType', 'Любой тип')}</MenuItem>
            {TYPES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`exercises.types.${value}.name`)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={t('exercises.picker.source', 'Источник')}
            value={source}
            onChange={(event) => {
              setSource(event.target.value as ExerciseSource | '')
              setPage(0)
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('exercises.picker.sourceAny', 'Все задания')}</MenuItem>
            <MenuItem value="own">{t('exercises.picker.sourceOwn', 'Мои')}</MenuItem>
            <MenuItem value="system">{t('exercises.picker.sourceSystem', 'EasyA')}</MenuItem>
          </TextField>
        </Stack>

        {loading && <Skeleton variant="rounded" height={280} />}

        {!loading && items.length === 0 && total === 0 && (
          <Typography color="text.secondary">
            {t(
              'exercises.picker.empty',
              'Ничего не нашлось. Заведите задание в банке заданий — потом его можно будет поставить в эту тему.',
            )}
          </Typography>
        )}

        {!loading && items.length > 0 && (
          <List dense disablePadding>
            {items.map((exercise) => {
              const isAttached = attached.has(exercise.id)
              // Черновик выдать нельзя, а поставить в тему — можно: разница
              // приходит режимом, а не статусом задания
              const notReady = requireReady && exercise.status !== 'ready'

              return (
                <ListItemButton
                  key={exercise.id}
                  divider
                  // Пока запрос в полёте, погашен весь список: без этого
                  // учитель успевал кликнуть по соседней строке и получал
                  // вторую выдачу
                  disabled={isAttached || notReady || submittingId !== null}
                  onClick={() => void submit(exercise)}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {/* Название весомее бейджей: без этого строка
                          «название + чипы» читалась сплошным текстом */}
                        <Typography component="span" fontWeight={600}>
                          {exercise.title}
                        </Typography>
                        {/* variant="outlined" обязателен: заливка чипа по
                          умолчанию совпадает с фоном диалога, и тип задания
                          на экране просто не виден */}
                        <Chip size="small" variant="outlined" label={t(`exercises.types.${exercise.type}.name`)} />
                        {/* «Готово» — норма, а не новость: показываем только
                          то, что мешает поставить задание в тему */}
                        {exercise.status !== 'ready' && (
                          <Chip
                            size="small"
                            color={exercise.status === 'draft' ? 'warning' : 'default'}
                            label={t(`exercises.exerciseStatus.${exercise.status}`)}
                          />
                        )}
                        {exercise.is_system && (
                          <Chip size="small" color="secondary" label={t('exercises.bank.systemBadge', 'EasyA')} />
                        )}
                        {isAttached && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('exercises.picker.attached', 'Уже в теме')}
                          />
                        )}
                      </Stack>
                    }
                    secondary={exercise.instructions}
                  />
                  {/* Спиннер на нажатой строке: погашенный список без него
                      читается как зависший, и учитель бьёт по нему снова */}
                  {submittingId === exercise.id && <CircularProgress size={18} sx={{ ml: 1 }} />}
                </ListItemButton>
              )
            })}
          </List>
        )}

        {/* Пагинация вне ветки со списком: раньше она жила внутри неё и на
            каждом перелистывании исчезала под скелетоном — контрол прыгал
            из-под курсора, а страница, опустевшая после того как задание
            поставили в тему, становилась тупиком: вернуться назад было нечем,
            не закрывая диалог. */}
        <TablePagination
          component="div"
          count={total}
          // Номер в пределах выдачи: страницу за её пределами догоняет
          // эффект-кламп, а MUI на такой page ругается в консоль
          page={total === 0 ? 0 : Math.min(page, Math.ceil(total / perPage) - 1)}
          onPageChange={(_, value) => setPage(value)}
          rowsPerPage={perPage}
          rowsPerPageOptions={[20, 50, 100]}
          onRowsPerPageChange={(event) => {
            setPerPage(parseInt(event.target.value, 10))
            setPage(0)
          }}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of')} ${count}`}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel', 'Отмена')}</Button>
      </DialogActions>
    </Dialog>
  )
}
